<script setup lang="ts">
import { computed } from 'vue';
import StatIcon from './StatIcon.vue';
import {
  compare,
  fillFraction,
  formatStat,
  type StatDescriptor,
} from './stat-display';

/**
 * A block of stat bars, optionally compared against what is currently fitted.
 *
 * The comparison is the whole point of the shop screen: an isolated "48 damage"
 * means nothing, "48 damage, +28 on what you have, but 1.3 s slower to reload"
 * is a decision.
 */
const props = defineProps<{
  descriptors: StatDescriptor[];
  values: Record<string, number>;
  /** When present, each row shows the delta against it. */
  against?: Record<string, number> | null;
  /**
   * Draw a glyph per row.
   *
   * Opt-in, and off by default, because this component is the readout on five
   * screens: the garage panel, the shop sidebar, an item card, the workbench and
   * the customise window. Icons earn their room on the panel — it is a standing
   * readout a player scans rather than reads, and it is the one place with the
   * width to spend. On a card in a grid the same column would take a fifth of a
   * narrow row from the label it is illustrating, so those keep their text.
   */
  icons?: boolean;
}>();

const rows = computed(() =>
  props.descriptors
    .filter((d) => typeof props.values[d.key] === 'number')
    .map((descriptor) => {
      const value = props.values[descriptor.key];
      const reference = props.against?.[descriptor.key];
      const hasReference = typeof reference === 'number';
      const direction = hasReference ? compare(descriptor, value, reference) : 0;

      return {
        descriptor,
        value,
        text: formatStat(descriptor, value),
        fill: fillFraction(descriptor, value),
        referenceFill: hasReference ? fillFraction(descriptor, reference) : 0,
        direction,
        delta: hasReference ? value - reference : 0,
        deltaText: hasReference
          ? `${value - reference > 0 ? '+' : '−'}${(
              Math.abs(value - reference) * (descriptor.displayScale ?? 1)
            ).toFixed(descriptor.decimals ?? 0)}`
          : '',
      };
    }),
);
</script>

<template>
  <dl class="stats" :class="{ iconed: props.icons }">
    <div v-for="row in rows" :key="row.descriptor.key" class="row" :data-stat="row.descriptor.key">
      <span v-if="props.icons" class="icon"><StatIcon :stat="row.descriptor.key" /></span>
      <dt>{{ row.descriptor.label }}</dt>
      <dd>
        <span class="value">{{ row.text }}</span>
        <span
          v-if="row.direction !== 0"
          class="delta"
          :class="row.direction > 0 ? 'better' : 'worse'"
        >
          {{ row.deltaText }}
        </span>
      </dd>
      <div class="track">
        <i v-if="against" class="ghost" :style="{ width: `${row.referenceFill * 100}%` }"></i>
        <i
          class="bar"
          :class="{ better: row.direction > 0, worse: row.direction < 0 }"
          :style="{ width: `${row.fill * 100}%` }"
        ></i>
      </div>
    </div>
  </dl>
</template>

<style scoped>
.stats {
  margin: 0;
  display: grid;
  gap: 0.7rem;
}

.row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.15rem 0.5rem;
}

/*
 * With icons the row grows a leading column and the glyph spans both of the
 * row's lines — the label and the bar are one stat, so a mark that sat on the
 * label alone would leave the bar looking like a separate item. The track keeps
 * spanning to the end, which is why it is addressed by name here rather than
 * left on `1 / -1`: that shorthand would put it under the glyph too.
 */
.iconed .row {
  grid-template-columns: auto 1fr auto;
}

.icon {
  grid-row: 1 / 3;
  align-self: center;
  flex: none;
  display: grid;
  place-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 4px;
  background: rgba(232, 114, 44, 0.13);
  color: var(--accent);
  padding: 0.3rem;
}

.iconed .track {
  grid-column: 2 / -1;
}

dt {
  font-size: 0.7rem;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--steel-400);
}

dd {
  margin: 0;
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-family: ui-monospace, 'SF Mono', monospace;
  color: #fff;
}

.delta {
  font-size: 0.7rem;
}

.delta.better {
  color: #4caf6d;
}

.delta.worse {
  color: #d9705f;
}

.track {
  grid-column: 1 / -1;
  position: relative;
  height: 4px;
  border-radius: 2px;
  background: var(--steel-800);
  overflow: hidden;
}

.track i {
  position: absolute;
  inset: 0 auto 0 0;
  display: block;
  border-radius: 2px;
}

.ghost {
  background: var(--steel-600);
}

.bar {
  background: var(--accent);
}

.bar.better {
  background: #4caf6d;
}

.bar.worse {
  background: #8a5a4a;
}
</style>
